import { getEntityList } from "helpers/utils";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Entity } from "types";

interface EntityListResult<T> {
  list: T[];
  totalCount: number;
  page: number;
  pages: number;
  isLoading: boolean;
  error: Error | null;
  setPage: (page: number) => void;
  findAndUpdate: (id: string | number, updatedItem: Partial<T>) => void;
  findAndDelete: (id: string | number) => void;
  refetch: () => void;
}

interface UseEntitiesOptions {
  entity: Entity;
  pageSize?: number;
  sort?: string[];
  fields?: string[];
  search?: string;
  paginate?: boolean;
  deep?: Record<string, any>;
  filter?: Record<string, any>;
}

const SORT: string[] = [];
const FIELDS = ["*"];
const DEEP = {};
const FILTER = {};

export function useEntities<T>({
  entity,
  pageSize = 25,
  sort = SORT,
  fields = FIELDS,
  search = "",
  paginate = true,
  deep = DEEP,
  filter = FILTER,
}: UseEntitiesOptions): EntityListResult<T> {
  const [searchParams] = useSearchParams();

  const initialPage = parseInt(searchParams.get("page") || "1", 10);

  const [page, setPage] = useState(initialPage);
  const [list, setList] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pages, setPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEntities = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await getEntityList({
        entity,
        page,
        pageSize,
        sort,
        fields,
        search,
        paginate,
        deep,
        filter,
      });

      if (result) {
        setList(result.list as T[]);
        setTotalCount(result.totalCount);
        setPages(result.pages);
      } else {
        setList([]);
        setPage(1);
      }

      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch entities")
      );
      setList([]);
    } finally {
      setIsLoading(false);
    }
  }, [entity, page, pageSize, sort, fields, search, paginate, deep, filter]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  const findAndUpdate = (id: number | string, updatedItem: Partial<T>) => {
    setList((currentList) => {
      const index = currentList.findIndex((item) => (item as any).id === id);
      if (index === -1) {
        return [{ ...(updatedItem as T) }, ...currentList];
      }
      return currentList.map((item) =>
        (item as any).id === id ? { ...item, ...updatedItem } : item
      );
    });
  };

  const findAndDelete = (id: number | string) => {
    setList((currentList) =>
      currentList.filter((item) => (item as any).id !== id)
    );
  };

  const refetch = () => {
    fetchEntities();
  };

  return {
    list,
    totalCount,
    page,
    pages,
    isLoading,
    error,
    setPage,
    findAndUpdate,
    findAndDelete,
    refetch,
  };
}
